import { useCallback, useEffect, useRef, useState } from "react";

export function usePolling(
  callback: () => void | Promise<void>,
  interval: number = 5000
) {
  const [polling, setPolling] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);

  const start = useCallback(() => {
    if (!pollingRef.current) {
      pollingRef.current = true;
      setPolling(true);
    }
  }, []);

  const stop = useCallback(() => {
    pollingRef.current = false;
    setPolling(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    pollingRef.current = polling;

    if (!polling) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (!pollingRef.current || cancelled) return;
      await callback();
      if (!pollingRef.current || cancelled) return;
      timeoutRef.current = setTimeout(poll, interval);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [polling, callback, interval]);

  return { start, stop, polling };
}
